/**
 * DocLinkNode - 本文中の [[リンク]]。メモか作品（読書ノート）を指し、タップでその画面を開く。
 *
 * - 属性は targetType / targetId と表示名のスナップショット (label)
 * - 保存時に BE が本文からリンクを拾い、リンク先のバックリンクに出す
 */

import { mergeAttributes, Node } from '@tiptap/core'
import type { NodeViewProps } from '@tiptap/react'
import { ReactNodeViewRenderer } from '@tiptap/react'

import type { LinkTargetType } from '@/api/services/links'

import { DocLinkNodeView } from './doc-link-node-view'

export type DocLinkAttrs = { targetType: LinkTargetType; targetId: number; label: string }

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    docLink: {
      /** カーソル位置にリンクを挿入する（後ろに空白を入れて続けて書けるようにする） */
      insertDocLink: (attrs: DocLinkAttrs) => ReturnType
    }
  }
}

export const DocLinkNode = Node.create({
  name: 'docLink',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      targetType: {
        default: 'memo' as LinkTargetType,
        parseHTML: (el) => (el.getAttribute('data-target-type') === 'product' ? 'product' : 'memo'),
        renderHTML: (attrs) => ({ 'data-target-type': String(attrs.targetType) }),
      },
      targetId: {
        default: null as number | null,
        parseHTML: (el) => {
          const n = Number(el.getAttribute('data-target-id'))
          return Number.isInteger(n) && n > 0 ? n : null
        },
        renderHTML: (attrs) => (attrs.targetId != null ? { 'data-target-id': String(attrs.targetId) } : {}),
      },
      label: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-label') ?? el.textContent ?? '',
        renderHTML: (attrs) => ({ 'data-label': String(attrs.label ?? '') }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-type="doc-link"]' }]
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes({ 'data-type': 'doc-link' }, HTMLAttributes), `[[${node.attrs.label}]]`]
  },

  renderText({ node }) {
    return `[[${node.attrs.label}]]`
  },

  addNodeView() {
    return ReactNodeViewRenderer(DocLinkNodeView as (props: NodeViewProps) => React.JSX.Element)
  },

  addCommands() {
    return {
      insertDocLink:
        (attrs) =>
        ({ chain }) =>
          chain()
            .insertContent([
              { type: this.name, attrs },
              { type: 'text', text: ' ' },
            ])
            .run(),
    }
  },
})
